import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ToggleButton from '@Components/Results/reactions/reaction-details/ToggleButton';
import ReactionCIStageDetailsComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-ci-stage-details.component';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import ReactionStageDetailComponent from '@Components/Results/reactions/reaction-details/reaction-stage-detail.component';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ConditionsReferenceExperimentComponent from '@Components/Results/reactions/reaction-details/ConditionsReferenceExperiment';
import ReactionAdditionalInfoComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-additional-info.component';
import { openMultiStepReactionDialog } from '@Pages/RetroSynthesis/MultiStepReactionDialog/actions';
import { ButtonWithIcon } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import { CITATION_LINKS_TRACKING_INDEX_MARKER } from '@Components/Results/analytics/citationLinksTracking';
import InSummaryConditionIndicatorContainer from '@Containers/Summary/InSummaryConditionIndicatorContainer';

import './ReactionDetailRow.scss';

function renderSummaryConditionIndicator(summaryConditionId, reactionId, databaseId, metadata) {
  if (!summaryConditionId) return null;

  return (
    <InSummaryConditionIndicatorContainer
      reactionId={reactionId}
      conditionId={summaryConditionId}
      databaseId={databaseId}
      resultName={metadata.resultName}
      fromRT={metadata.fromRT}
    />
  );
}

function ReactionDetailRow({
  metadata,
  detail,
  rtProjectId,
  rtRouteId,
  rtParentId,
  rtStepId,
  index,
  largestProduct,
  onFullTextLinkClick = () => null,
  onIssueCitedCounterClick = () => null,
  onAbstractOpen = () => null,
  onAuthorClick = () => null,
  databaseId,
  reactionId,
  renderCheckboxesColumn = false,
  isChecked,
  isVisible = true,
  setChecked,
  allowAdditionalInfoShow = true,
  onExperimentalProcedureOpen = () => null,
  onRenderedRowsNumberChange = () => null,
  showReferenceColumn = true,
  summaryConditionId,
}) {
  const [experimentalProcedureExpanded, setExperimentalProcedureExpanded] = useState(false);
  const [referencesForcedShow, setReferencesForcedShow] = useState(false);
  const schemeLoadingRef = useRef(false);

  const isCIDatabase = ReactReactionAdditionalInfoHelper.isCIDatabase(databaseId);
  const additionalInfoVisible =
    allowAdditionalInfoShow && isCIDatabase && ReactReactionAdditionalInfoHelper.isAdditionalInfoVisible(detail);
  const showReferenceDetails = showReferenceColumn && (!isEmpty(detail.citations) || detail.elnCitation || detail.experiment);
  const fullRowColSpan = (renderCheckboxesColumn ? 2 : 3) + (showReferenceColumn ? 1 : 0);

  const recalculateRenderedRowsNumber = useCallback(() => {
    const renderedRows =
      1 + (experimentalProcedureExpanded ? 1 : 0) + (showReferenceColumn && referencesForcedShow ? 2 : 0) + (additionalInfoVisible ? 1 : 0);
    onRenderedRowsNumberChange(renderedRows);
  }, [experimentalProcedureExpanded, referencesForcedShow, additionalInfoVisible, onRenderedRowsNumberChange, showReferenceColumn]);

  useEffect(() => {
    recalculateRenderedRowsNumber();
  }, [recalculateRenderedRowsNumber]);

  const getYields = () => {
    const yields = get(detail, 'yields', null);
    return yields
      ? yields.map((yiel, yieldIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="yield-row" key={yieldIndex}>
            {yields.length > 1 && <strong>{ReactReactionDetailsHelper.getLetterTitle(yieldIndex)}&nbsp;</strong>}
            <InnerHTML tag="span" html={yiel?.yieldValue || 'n/a'} />
          </div>
        ))
      : null;
  };

  const getMultiStepDetails = () => {
    const multiStepDetails = get(detail, 'multiStepDetails', null);
    return multiStepDetails
      ? multiStepDetails.map((step, stepIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <InnerHTML tag="div" key={stepIndex} className="steps-row" html={step} mode="escape" />
        ))
      : null;
  };

  const getExampleLabelTitle = () => {
    const lbl = detail.exampleLabel;
    const ttl = detail.exampleTitle;
    return `${lbl || ''} ${ttl || ''}`.trim() || 'Experimental Procedure';
  };

  const getTrackingProps = () => {
    const trackingProps = {};
    if (!metadata.oldTrackingDisabled) {
      trackingProps.prepareTrackingData = isEmpty(detail.citations) ? null : detail.citations?.[0];
      trackingProps.dataTrackingAction = 'experimental procedure';
    }
    return trackingProps;
  };

  const toggleExperimentalProcedureExpanded = () => {
    setExperimentalProcedureExpanded((prev) => {
      const newValue = !prev;
      if (newValue && !isEmpty(detail.citations)) {
        onExperimentalProcedureOpen(detail.citations?.[0]);
      }
      return newValue;
    });
  };

  const toggleReferencesForcedShow = () => {
    setReferencesForcedShow((prev) => !prev);
  };

  const goToMultipleStepScheme = () => {
    if (!schemeLoadingRef.current) {
      schemeLoadingRef.current = true;
      store.dispatch(
        openMultiStepReactionDialog({
          databaseId: metadata?.databaseId,
          scheme: detail?.scheme,
          onClose: () => {
            schemeLoadingRef.current = false;
          },
          rtProjectId,
          rtRouteId,
          rtParentId,
          rtStepId,
        })
      );
    }
  };

  const mainRowClasses = classnames([
    'c-els-table__row',
    experimentalProcedureExpanded ? 'no-border-bottom rx-reactions-table__row-expanded' : null,
    isVisible ? null : 'rx-reactions-table__row-not-visible',
  ]);
  const summaryConditionIndicator = renderSummaryConditionIndicator(summaryConditionId, reactionId, databaseId, metadata);

  return (
    <>
      <tr
        className={mainRowClasses}
        data-summary-condition-id={summaryConditionId}
        tabIndex={summaryConditionId ? -1 : undefined}
        {...(!referencesForcedShow && CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem)}>
        {/* Conditions section */}
        {renderCheckboxesColumn && (
          <td className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__checkbox">
            <div className="result-checkbox-container">
              <Checkbox
                data-e2e="select-condition"
                id={`checkbox-${reactionId}_${index}`}
                label={`Select condition number ${Number(index) + 1}`}
                shouldHideLabel
                checked={isChecked}
                onChange={() => setChecked(detail.id, !isChecked)}
              />
              <div className="result-checkbox-container__index u-els-font-size-meta u-els-color-n8 u-els-text-center">
                {Number(index) + 1}
              </div>
            </div>
          </td>
        )}
        <td className="c-els-table__cell c-els-table__basic-cell">
          <div className="rx-reactions-table__conditions__steps">
            {detail.multiStepDetails ? (
              <>
                <div className="steps-row">
                  {summaryConditionIndicator}
                  <InnerHTML tag="b" html={detail.classification?.[0]} extendedTags />
                  <b> with </b>
                  <InnerHTML tag="b" html={detail.stepsCount} extendedTags />
                  <b> steps </b>
                </div>
                {getMultiStepDetails()}
                {detail.scheme && (
                  <ButtonWithIcon
                    type="link"
                    size="small"
                    sprite="ChevronRight"
                    iconRight
                    linkIconSize="xs"
                    data-e2e="view-multi-step-scheme"
                    className="e2e-view-scheme"
                    onClick={goToMultipleStepScheme}>
                    {rtStepId ? 'View/Insert Scheme' : 'View Scheme'}
                  </ButtonWithIcon>
                )}
              </>
            ) : (
              <>
                {detail.stages?.map((stage, stageIndex, stages) => (
                  /* eslint-disable react/no-array-index-key */
                  <ReactionStageDetailComponent
                    metadata={metadata}
                    key={stageIndex}
                    index={index}
                    stage={stage}
                    stageIndex={stageIndex}
                    isCIDatabase={isCIDatabase}
                    hasMultipleStages={stages.length > 1}
                    leadingContent={stageIndex === 0 ? summaryConditionIndicator : null}
                  />
                  /* eslint-enable react/no-array-index-key */
                ))}

                {isCIDatabase && <ReactionCIStageDetailsComponent largestProduct={largestProduct} detail={detail} />}
              </>
            )}
          </div>

          {/* Toggle buttons (Experimental Procedure and Reference) */}
          <div className="u-els-display-flex u-els-margin-top-1o4">
            {detail.experimentalProcedure && (
              <ToggleButton
                className="rx-reactions-table__content-toggle u-els-margin-right-1o2 e2e-experimental-procedure"
                ariaControls={`reaction-procedure-${index}`}
                expanded={experimentalProcedureExpanded}
                toggleExpand={toggleExperimentalProcedureExpanded}
                expandedLabel="Experimental Procedure"
                collapsedLabel="Experimental Procedure"
                isViewPreview={metadata.isViewPreview}
                {...getTrackingProps()}
              />
            )}
            {showReferenceDetails && (
              <ToggleButton
                className="rx-reactions-table__content-toggle u-els-display-none u-els-display-table@tablet e2e-show-hide-reference"
                ariaControls={`reaction-references-${index}`}
                expanded={referencesForcedShow}
                toggleExpand={toggleReferencesForcedShow}
                expandedLabel={isCIDatabase ? 'Hide Reference/Experiment' : 'Hide Reference'}
                collapsedLabel={isCIDatabase ? 'Show Reference/Experiment' : 'Show Reference'}
                isViewPreview={metadata.isViewPreview}
              />
            )}
          </div>
        </td>

        {/* Yields section */}
        <td className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__yield">{getYields()}</td>

        {/* Reference/Experiment section - wide screen */}
        {showReferenceColumn && (
          <td className="c-els-table__cell c-els-table__basic-cell u-els-display-none@tablet" id={`reaction-references-${index}`}>
            <ConditionsReferenceExperimentComponent
              metadata={metadata}
              index={index}
              detail={detail}
              onFullTextLinkClick={onFullTextLinkClick}
              onIssueCitedCounterClick={onIssueCitedCounterClick}
              onAbstractOpen={onAbstractOpen}
              onAuthorClick={onAuthorClick}
            />
          </td>
        )}
      </tr>

      {/* Experimental procedure section */}
      {experimentalProcedureExpanded && (
        <tr className={` c-els-table__row display-table-row ${isVisible ? '' : 'rx-reactions-table__row-not-visible'}`}>
          {renderCheckboxesColumn && <th className="rx-reactions-table__extra" />}
          <td className="table__cell c-els-table__cell c-els-table__basic-cell" colSpan={fullRowColSpan} id={`reaction-procedure-${index}`}>
            <div className="rx-reactions-table__procedure-panel u-els-padding-1o2">
              <h4 className="rx-reactions-table__header u-els-margin-bottom-1o2 u-els-font-size-base u-els-font-family-bold">
                {getExampleLabelTitle()}
              </h4>
              <InnerHTML
                tag="div"
                className="rx-reactions-table__content u-els-padding-none"
                html={detail.experimentalProcedure}
                extendedTags
              />
            </div>
          </td>
        </tr>
      )}

      {/* Reference/Experiment section - toggled on small screen */}
      {showReferenceColumn && referencesForcedShow && (
        <>
          <tr className=" c-els-table__row rx-reactions-table__header display-table-row display-table-row-at-tablet">
            <td colSpan="4" className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__references">
              {isCIDatabase ? 'Reference/Experiment' : 'Reference'}
            </td>
          </tr>

          <tr
            className="c-els-table__row rx-reactions-table__item display-table-row-at-tablet"
            {...CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem}>
            <td className="c-els-table__cell c-els-table__basic-cell" colSpan="4" id={`reaction-references-${index}`}>
              <ConditionsReferenceExperimentComponent
                metadata={metadata}
                index={index}
                detail={detail}
                onFullTextLinkClick={onFullTextLinkClick}
                onIssueCitedCounterClick={onIssueCitedCounterClick}
                onAbstractOpen={onAbstractOpen}
                onAuthorClick={onAuthorClick}
              />
            </td>
          </tr>
        </>
      )}

      {/* Additional CI info section */}
      {additionalInfoVisible && (
        <tr className="c-els-table__row rx-reactions-table__item display-table-row">
          <td className=" c-els-table__cell c-els-table__basic-cell rx-reactions-table__additional-info" colSpan={fullRowColSpan}>
            <ReactionAdditionalInfoComponent detail={detail} databaseId={databaseId} index={index} reactionId={reactionId} />
          </td>
        </tr>
      )}
    </>
  );
}

ReactionDetailRow.propTypes = {
  metadata: MetadataPropTypesShape,
  detail: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  reactionId: PropTypes.string.isRequired,
  databaseId: PropTypes.string.isRequired,
  rtProjectId: PropTypes.string,
  rtRouteId: PropTypes.string,
  rtParentId: PropTypes.string,
  rtStepId: PropTypes.string,
  isHit: PropTypes.bool,
  spinnerId: PropTypes.string,
  largestProduct: PropTypes.string,
  onExperimentalProcedureOpen: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
  allowAdditionalInfoShow: PropTypes.bool,
  renderCheckboxesColumn: PropTypes.bool,
  isChecked: PropTypes.bool,
  isVisible: PropTypes.bool,
  setChecked: PropTypes.func,
  onRenderedRowsNumberChange: PropTypes.func,
  showReferenceColumn: PropTypes.bool,
  summaryConditionId: PropTypes.string,
};

export default ReactionDetailRow;
